import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, FilePlus2, LayoutTemplate } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Radio } from '../../../components/Checkbox';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatIsoDate } from '../../../lib/format';
import { useCanEdit } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useCreateJob, useJobStart } from '../api/jobs-api';
import styles from './JobStartPage.module.css';

/**
 * JOB-02 before the job exists — where N-08 "approved, no job created" lands (§L22),
 * and where JOB-01's "start a job" goes.
 *
 * One click for the lazy path (CLAUDE.md §4): pick a template, or none, and create.
 * The job is created here rather than on arrival, so opening a reminder never makes
 * a job by accident.
 */
export function JobStartPage(): ReactNode {
  const { requisitionId } = useParams({ from: '/app-layout/jobs/new/$requisitionId' });
  const start = useJobStart(requisitionId);
  const create = useCreateJob();
  const canCreate = useCanEdit('job_config');
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const [templateId, setTemplateId] = useState<string>('');

  if (!canCreate) return <ForbiddenState resource="job setup" contact="Your admin" />;
  if (start.isPending) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={6} />
      </div>
    );
  }
  if (start.error) {
    if (start.error.status === 403) return <ForbiddenState resource="this requisition" contact="Your admin" />;
    if (start.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void start.refetch()} />;
  }

  const { requisition, existingJobId, templates } = start.data;

  async function onCreate(): Promise<void> {
    try {
      const created = await create.mutateAsync({ requisitionId, roleTemplateId: templateId === '' ? null : templateId });
      show({ tone: 'success', message: 'Job created', detail: `${requisition.reference} is now this job's requisition.` });
      void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: created.id, section: 'basics' } });
    } catch (cause) {
      show({ tone: 'danger', message: 'The job was not created', detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.page}>
      <Link to="/jobs" className={styles.back}>
        <ArrowLeft size={14} aria-hidden />
        Jobs
      </Link>
      <h1 className={styles.title}>Set up the job for {requisition.title}</h1>

      <Card title="Approved requisition" meta={`${requisition.reference} · ${requisition.departmentName} · ${requisition.ventureName}`}>
        <dl className={styles.facts}>
          <Fact label="Hires" value={String(requisition.slotsRequested)} />
          <Fact label="Hiring manager" value={requisition.hiringManagerName ?? EMPTY} />
          <Fact label="Recruiter" value={requisition.recruiterName ?? EMPTY} />
          <Fact label="Earliest start" value={formatIsoDate(requisition.targetStartDate)} />
          <Fact label="Latest start" value={formatIsoDate(requisition.targetStartLatest)} />
        </dl>
      </Card>

      {existingJobId ? (
        <Card title="This requisition already has its job">
          <div className={styles.actions}>
            <Button
              variant="solid"
              icon={ArrowRight}
              onClick={() =>
                void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: existingJobId, section: 'basics' } })
              }
            >
              Open the job
            </Button>
          </div>
        </Card>
      ) : (
        <Card title="Start from" meta="A template fills the description, ideal candidate, form and pipeline. You can change any of it">
          <div className={styles.choices} role="radiogroup" aria-label="Start from">
            {templates.map((template) => (
              <Radio
                key={template.id}
                name="template"
                value={template.id}
                checked={templateId === template.id}
                label={template.name}
                description={[template.roleFamily, template.departmentName ?? template.ventureName ?? 'Every department']
                  .filter(Boolean)
                  .join(' · ')}
                onChange={setTemplateId}
              />
            ))}
            <Radio
              name="template"
              value=""
              checked={templateId === ''}
              label="A blank job"
              description="The standard pipeline and form, nothing else filled in."
              onChange={setTemplateId}
            />
          </div>
          <div className={styles.actions}>
            <Button
              variant="solid"
              icon={templateId === '' ? FilePlus2 : LayoutTemplate}
              isPending={create.isPending}
              onClick={() => void onCreate()}
            >
              Create the job
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}
